import React from 'react';
import { ArrowRight, Star, Gift, Newspaper } from 'lucide-react';
import { heroStats, tickerHeadlines } from '../mock';

const PhoneMock = () => (
  <div className="relative floaty">
    {/* Reward badge */}
    <div className="absolute -top-5 -left-6 z-20 glow-pulse bg-[var(--ydd-yellow)] text-[#0E0E11] rounded-2xl px-4 py-2.5 flex items-center gap-2 shadow-xl floaty-slow">
      <Gift size={18} />
      <div className="leading-tight">
        <div className="font-display text-[13px]">+5 pts</div>
        <div className="text-[10px] font-semibold opacity-70">Article read</div>
      </div>
    </div>
    {/* Phone frame */}
    <div className="relative w-[288px] h-[588px] rounded-[2.6rem] bg-[#000] border-[10px] border-[#1c1c22] shadow-[0_40px_90px_-30px_rgba(0,0,0,0.9)] overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-28 h-6 bg-[#1c1c22] rounded-b-2xl z-20" />
      {/* Screen */}
      <div className="h-full w-full bg-[#0E0E11] flex flex-col">
        <div className="pt-8 px-4 pb-3 flex items-center justify-between">
          <span className="font-display text-white text-[12px]">YDD<span className="text-[var(--ydd-yellow)]">.News</span></span>
          <div className="flex items-center gap-1 bg-white/10 rounded-full px-2.5 py-1">
            <Gift size={12} className="text-[var(--ydd-yellow)]" />
            <span className="text-[11px] text-white font-bold">1,240</span>
          </div>
        </div>
        <div className="relative flex-1">
          <img src="https://images.unsplash.com/photo-1730739463889-34c7279277a9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwzfHxjcmlja2V0JTIwc3RhZGl1bXxlbnwwfHx8fDE3ODk2NTg4NDF8MA&ixlib=rb-4.1.0&q=85" alt="news" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0E0E11] via-[#0E0E11]/30 to-transparent" />
          <div className="absolute top-3 left-3 bg-red-600 text-white text-[9px] font-bold px-2 py-1 rounded uppercase tracking-wide">Breaking</div>
          <div className="absolute bottom-0 p-4">
            <h4 className="text-white font-extrabold text-[15px] leading-snug">India clinch a thriller in the final over</h4>
            <p className="text-gray-300 text-[11px] mt-1.5 leading-relaxed">A stunning finish seals the series as the crowd erupts in celebration.</p>
            <div className="mt-3 flex items-center gap-2">
              <span className="text-[10px] text-[var(--ydd-yellow)] font-semibold">Swipe up for next</span>
            </div>
          </div>
        </div>
        <div className="h-1 w-24 bg-white/40 rounded-full mx-auto mb-3" />
      </div>
    </div>
  </div>
);

const Hero = () => {
  return (
    <section id="home" className="relative pt-[68px] overflow-hidden grain">
      {/* ambient glows */}
      <div className="pointer-events-none absolute -top-32 -right-24 w-[520px] h-[520px] rounded-full bg-[var(--ydd-yellow)]/10 blur-[120px]" />
      <div className="pointer-events-none absolute top-40 -left-24 w-[420px] h-[420px] rounded-full bg-[var(--ydd-yellow)]/5 blur-[120px]" />

      <div className="container-ydd relative grid lg:grid-cols-2 gap-14 items-center py-16 md:py-24">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3.5 py-1.5 text-xs text-gray-200">
            <span className="h-2 w-2 rounded-full bg-[var(--ydd-yellow)] animate-pulse" />
            India's rewarding news app
          </div>
          <h1 className="font-display text-white mt-6 text-[2.6rem] sm:text-6xl leading-[1.05]">
            Read News.<br />Earn <span className="text-[var(--ydd-yellow)]">Rewards.</span><br />Save More.
          </h1>
          <p className="text-gray-400 mt-6 text-lg max-w-md leading-relaxed">
            Bite-sized news across every category that keeps you informed — while earning points you redeem for exclusive brand discounts every day.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <a href="#download" className="btn-yellow px-7 py-3.5 text-[15px] flex items-center gap-2">
              Get Started <ArrowRight size={18} />
            </a>
            <a href="#news" className="btn-outline-ydd px-7 py-3.5 text-[15px] flex items-center gap-2">
              <Newspaper size={18} /> Explore News
            </a>
          </div>
          <div className="mt-12 flex items-center gap-8">
            {heroStats.map((s, i) => (
              <div key={i} className="flex flex-col">
                <span className="font-display text-white text-2xl flex items-center gap-1">
                  {s.value}{s.label.includes('Rating') && <Star size={16} className="text-[var(--ydd-yellow)] fill-[var(--ydd-yellow)]" />}
                </span>
                <span className="text-gray-500 text-xs mt-1">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-center lg:justify-end">
          <PhoneMock />
        </div>
      </div>

      {/* Breaking ticker */}
      <div className="relative border-y border-white/10 bg-[#16161B] overflow-hidden">
        <div className="flex">
          <div className="shrink-0 bg-[var(--ydd-yellow)] text-[#0E0E11] font-display text-[11px] px-5 flex items-center uppercase tracking-wider z-10">Live</div>
          <div className="overflow-hidden flex-1">
            <div className="marquee-track py-3">
              {[...tickerHeadlines, ...tickerHeadlines].map((h, i) => (
                <span key={i} className="text-sm text-gray-300 px-8 flex items-center gap-8 whitespace-nowrap">
                  {h} <span className="text-[var(--ydd-yellow)]">/</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
